// The share modal (handoff "2b", docs/design/lists-add-and-share/README.md, Screen 2): pick a channel
// (Email / Text / Copy link), add recipients in one chip field, see exactly what they'll receive, send,
// and see who it was sent to before. Used for a list (ShareReportModal → listSubject) and for one athlete
// (athleteSubject). Styles: pages/ShareReportModal.css.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import RecipientField, { chipError, plainChip } from './RecipientField';
import '../../pages/ShareReportModal.css';

const Icon = ({ children }) => (
  <svg className="ls-share__icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const ICONS = {
  email: <Icon><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></Icon>,
  text: <Icon><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Icon>,
  link: <Icon><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Icon>,
};
const CHANNELS = [['email', 'Email'], ['text', 'Text'], ['link', 'Copy link']];

const channelSet = (r) => String(r.channels || '').split(',').map((s) => s.trim()).filter(Boolean);
const channelLabel = (r) => {
  const c = channelSet(r);
  const out = [c.includes('email') && 'email', c.includes('sms') && 'text'].filter(Boolean);
  return out.length ? out.join(' + ') : 'in app';
};
const recipientName = (r) => r.name || r.email || r.phone || 'Unknown';
const recipientSummary = (r) => `${recipientName(r)} · ${channelLabel(r)} · ${r.opened_at ? 'opened' : 'not opened yet'}`;
const newestFirst = (rows) => [...rows].sort((a, b) => String(b.sent_at || '').localeCompare(String(a.sent_at || '')));
const toPayload = (c) => (c.kind === 'coach' ? { coach_user_id: c.id } : c.kind === 'phone' ? { phone: c.value } : { email: c.value });

export default function ShareModal({ subject, onClose }) {
  const { API_URL, user } = useAuth();
  const canSearchCoaches = ['admin', 'internal_staff'].includes(user?.role);
  const cfg = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const [channel, setChannel] = useState('email');
  const [chips, setChips] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const flash = (set) => { set(true); timers.current.push(setTimeout(() => set(false), 1500)); };

  const takeServer = useCallback((data) => {
    setRecipients(newestFirst(data.recipients || []));
    if (typeof data.sms_enabled === 'boolean') setSmsEnabled(data.sms_enabled);
  }, []);

  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}${subject.historyPath}`, cfg())
      .then((res) => { if (live) takeServer(res.data || {}); })
      .catch(() => { /* history is non-fatal */ });
    return () => { live = false; };
  }, [API_URL, subject.historyPath, cfg, takeServer]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Text is off on the server: never leave it selected.
  useEffect(() => { if (!smsEnabled && channel === 'text') setChannel('email'); }, [smsEnabled, channel]);

  const pickChannel = (c) => {
    setChannel(c);
    setErr('');
    // A typed entry is re-read for the new channel (an email is not a phone number); coach chips stay.
    setChips((prev) => prev.map((x) => (x.kind === 'coach' ? x : plainChip(x.value, c))));
  };

  const valid = chips.filter((c) => !chipError(c, channel));

  const post = async (recipientsBody, sendEmail, sendSms) => {
    setErr('');
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}${subject.sendPath}`,
        { recipients: recipientsBody, send_email: sendEmail, send_sms: sendSms }, cfg());
      takeServer(res.data || {});
      return true;
    } catch (e) {
      // FastAPI's 422 detail is an array of objects — rendering it would crash the modal.
      const detail = e.response?.data?.detail;
      setErr(typeof detail === 'string' && detail ? detail : 'Could not send.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!valid.length || busy) return;
    if (await post(valid.map(toPayload), channel === 'email', channel === 'text')) {
      setChips([]);
      flash(setSent);
    }
  };

  // Resend to one past recipient over the channels it was last sent on (in-app only → both false).
  const resend = (r) => {
    const c = channelSet(r);
    // Only staff can name a coach by id (the server ignores a partner's coach_user_id), so everyone else
    // re-sends to the address they typed.
    const byId = r.coach_user_id && (canSearchCoaches || !(r.email || r.phone));
    const who = byId ? { coach_user_id: r.coach_user_id } : r.email ? { email: r.email } : { phone: r.phone };
    post([who], c.includes('email'), c.includes('sms'));
  };

  const copyLink = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(subject.link).catch(() => {});
    flash(setCopied);
  };

  const [line1, line2] = subject.previewLines(user?.first_name, channel);
  const latest = recipients[0];

  return (
    <div className="rb-modal-overlay" role="dialog" aria-modal="true" aria-label={subject.title}
      data-testid="share_overlay" onClick={onClose}>
      <div className="rb-modal ls-share" data-testid="share_modal" onClick={(e) => e.stopPropagation()}>
        <div className="ls-share__head">
          <h3 className="ls-share__title">{subject.title}</h3>
          <button type="button" className="ls-share__close" aria-label="Close" data-testid="share_close" onClick={onClose}>×</button>
        </div>

        <div className="ls-share__channels" role="group" aria-label="Send by">
          {CHANNELS.map(([key, label]) => {
            const off = key === 'text' && !smsEnabled;
            return (
              <button key={key} type="button" data-testid={`share_channel_${key}`} aria-pressed={channel === key}
                disabled={off} onClick={() => pickChannel(key)}
                className={channel === key ? 'ls-share__channel ls-share__channel--on' : 'ls-share__channel'}>
                <span className="ls-share__channel-title">{ICONS[key]}{label}</span>
                <span className="ls-share__channel-sub">{off ? 'Texting is turned off' : subject.subLines[key]}</span>
              </button>
            );
          })}
        </div>

        {channel === 'link' ? (
          <div className="ls-share__block">
            <span className="ls-link-pill ls-share__pill" data-testid="share_link_pill">
              <code className="ls-link-pill__code">{String(subject.link || '').replace(/^https?:\/\//, '')}</code>
              <button type="button" className="ls-btn ls-btn--tiny" data-testid="share_copy_link" onClick={copyLink}>
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </span>
          </div>
        ) : (
          <>
            <RecipientField channel={channel} chips={chips} onChange={setChips} canSearchCoaches={canSearchCoaches} autoFocus />
            <div className="ls-share__preview" data-testid="share_preview">
              <div className="ls-share__preview-head">What they'll receive</div>
              <div className="ls-share__preview-body" data-testid="share_preview_body">
                {line2 === undefined ? <div>{line1}</div> : (
                  <>
                    <div className="ls-share__preview-line1">{line1}</div>
                    <div>{line2}</div>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {err && <div role="alert" className="ls-banner ls-banner--error" data-testid="share_error">{err}</div>}

        {channel !== 'link' && (
          <div className="ls-share__foot">
            <span className="ls-share__hint">Coaches already on ProZpects also see it in the app.</span>
            <button type="button" className="ls-btn ls-btn--primary" data-testid="share_send"
              disabled={!valid.length || busy} onClick={send}>
              {sent ? 'Sent ✓' : `Send ${channel === 'text' ? 'text' : 'email'} to ${valid.length}`}
            </button>
          </div>
        )}

        {latest && (
          <div className="ls-share__history" data-testid="share_history">
            <div className="ls-share__history-row">
              <span className="ls-share__history-text">
                {recipients.length > 1 ? (
                  <button type="button" className="ls-share__history-toggle" data-testid="share_history_toggle"
                    aria-expanded={historyOpen} onClick={() => setHistoryOpen((v) => !v)}>
                    {`Sent before · ${recipients.length} ${historyOpen ? '▴' : '▾'}`}
                  </button>
                ) : <strong className="ls-share__history-count">{`Sent before · ${recipients.length}`}</strong>}
                {' '}{recipientSummary(latest)}
              </span>
              <button type="button" className="ls-share__resend" data-testid="share_resend" disabled={busy}
                onClick={() => resend(latest)}>Resend</button>
            </div>
            {historyOpen && recipients.length > 1 && (
              <div className="ls-share__history-list" data-testid="share_history_list">
                {recipients.map((r) => (
                  <div key={r.id} className="rb-modal-recipient">
                    <span>{recipientName(r)}</span>
                    <span className="rb-modal-recipient__meta">
                      {`${channelLabel(r)} · ${r.opened_at ? 'opened' : 'not opened yet'}`}
                      {r.forwarded_by && ` · via ${r.forwarded_by}`}
                      <button type="button" className="ls-share__resend" data-testid={`share_resend_${r.id}`} disabled={busy}
                        onClick={() => resend(r)}>Resend</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
